import {useCallback, useRef, useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import GenerateTicketReplyDraftMutation from '../../../../../mutations/ticketing/tickets/generateTicketReplyDraftMutation';

const NOT_CONFIGURED = 'AI drafting is not set up for this workspace yet. Ask an admin to enable it.';
const GENERIC = 'Could not draft a reply just now. Your message was left untouched — please try again.';

// The gateway's out-of-shared-AI-credit refusal. Matched strictly, so another
// refusal (e.g. a future GEN_AI_DISABLED 403) is never shown as out of credit.
export const OUT_OF_CREDIT = 'BUDGET_EXHAUSTED';

export interface DraftErrorExtensions {
	ticketingCode?: string;
	cap?: number;
	resetAt?: string;
}

// Formats a reset instant for the per-ticket cap copy. Returns null for missing
// or unparseable input so callers never emit "Invalid Date" / "undefined".
function formatResetAt(value?: string): string | null {
	if (!value) return null;
	const parsed = new Date(value);
	if (isNaN(parsed.getTime())) return null;
	const time = parsed.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
	const date = parsed.toLocaleDateString([], {month: 'short', day: 'numeric'});
	return `at ${time} on ${date}`;
}

/**
 * Turns a mutation error into copy an agent can act on.
 *
 * Covers the per-ticket cap, AI not configured, and generation failures. An
 * out-of-credit refusal never reaches here: it carries the gateway's own copy and
 * is surfaced as a `notice` instead (see useGenerateReplyDraft).
 */
export function friendlyDraftError(raw: string): string {
	if (/not configured|not set up/i.test(raw)) return NOT_CONFIGURED;

	const capMatch = /\((\d+) per day\)/.exec(raw);
	if (/limit reached|rate limit/i.test(raw) || capMatch) {
		const cap = capMatch ? capMatch[1] : null;
		const resetMatch = /\d{4}-\d{2}-\d{2}T[\d:.]+Z/.exec(raw);
		const when = formatResetAt(resetMatch ? resetMatch[0] : undefined);
		const capPart = cap ? `You have used all ${cap} AI drafts for this ticket today.` : 'Daily AI draft limit reached.';
		return when ? `${capPart} You can draft again ${when}.` : capPart;
	}

	return GENERIC;
}

interface GraphQLErrorEntry {
	message?: unknown;
	extensions?: unknown;
}

// Find the GraphQL error entry (message + extensions) on whatever shape the Relay
// error carries. commitMutation surfaces the failure as an Error, but the
// underlying entry can hang off a few different properties depending on the
// network layer: react-relay's own Error keeps them on `source.errors`,
// react-relay-network-modern parks the response on `res` (and wraps the Error's
// own message in "Relay request for … failed by the following reasons"), a bare
// GraphQLError exposes them at the top level, and an Apollo-style client uses
// `graphQLErrors`. Check each rather than assume one — a missed shape silently
// shows an out-of-credit refusal as an error.
function graphQLErrorEntry(err: unknown): GraphQLErrorEntry | undefined {
	if (!err || typeof err !== 'object') return undefined;
	const e = err as GraphQLErrorEntry & {
		source?: {errors?: GraphQLErrorEntry[]};
		res?: {errors?: GraphQLErrorEntry[]};
		graphQLErrors?: GraphQLErrorEntry[];
	};
	const candidates = [e, e.source?.errors?.[0], e.res?.errors?.[0], e.graphQLErrors?.[0]];
	return candidates.find(c => c?.extensions != null);
}

// Extract the ticketing extensions from the Relay error's GraphQL error entry.
export function extensionsFrom(err: unknown): DraftErrorExtensions | undefined {
	const raw = graphQLErrorEntry(err)?.extensions;
	if (!raw || typeof raw !== 'object') return undefined;
	const {ticketingCode, cap, resetAt} = raw as DraftErrorExtensions;
	if (ticketingCode === undefined && cap === undefined && resetAt === undefined) return undefined;
	return {ticketingCode, cap, resetAt};
}

// The gateway's own message, read from the same GraphQL error entry as
// extensionsFrom — not the wrapping Error's message, which the network layer may
// have decorated with Relay boilerplate. Only a bare GraphQLError (extensions at
// the top level) yields its own `message`.
export function gatewayMessageFrom(err: unknown): string | undefined {
	const message = graphQLErrorEntry(err)?.message;
	return typeof message === 'string' && message ? message : undefined;
}

interface Options {
	onCompleted?: (draftText: string) => void;
	onError?: (error: Error) => void;
}

/**
 * Requests an AI-drafted reply for a ticket.
 *
 * `generate` resolves with the draft text and rejects on failure, so the caller can
 * `await` it and only touch the editor on success. `error` carries display-ready
 * copy for the failure cases (over cap, AI not configured, generation failed).
 * `notice` carries the gateway's own message when the account is out of shared
 * AI credit — ordinary content, not a failure, so it is never put on `error`.
 */
export function useGenerateReplyDraft(options?: Options) {
	const [inFlight, setInFlight] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	// Keep the latest callbacks out of `generate`'s identity so wiring it to an
	// onClick does not re-create the handler on every parent render.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const reset = useCallback(() => {
		setError(null);
		setNotice(null);
	}, []);

	const generate = useCallback((ticketId: string): Promise<string> => {
		setInFlight(true);
		setError(null);
		setNotice(null);
		return new Promise<string>((resolve, reject) => {
			commitTicketingMutation(
				GenerateTicketReplyDraftMutation,
				{ticketId},
				(response: unknown) => {
					const draftText = (response as {generateTicketReplyDraft?: {draftText?: string}} | null)
						?.generateTicketReplyDraft?.draftText;
					setInFlight(false);
					if (!draftText) {
						const err = new Error(GENERIC);
						setError(GENERIC);
						optionsRef.current?.onError?.(err);
						reject(err);
						return;
					}
					optionsRef.current?.onCompleted?.(draftText);
					resolve(draftText);
				},
				(rawError: unknown) => {
					const err = rawError instanceof Error ? rawError : new Error(String(rawError));
					const extensions = extensionsFrom(rawError);
					const gatewayMessage = gatewayMessageFrom(rawError);
					setInFlight(false);
					// Out of shared AI credit: the gateway's own copy, shown as normal
					// content. It is not a failure of the composer, so never an error.
					if (extensions?.ticketingCode === OUT_OF_CREDIT && gatewayMessage) {
						setNotice(gatewayMessage);
					} else {
						setError(friendlyDraftError(err.message));
					}
					optionsRef.current?.onError?.(err);
					reject(err);
				}
			);
		});
	}, []);

	return {generate, inFlight, error, notice, reset};
}
